import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import moment from 'moment';
import { getDateFormat } from '../../utils/utils';
import ToolTip from '../ToolTip';

/**
 * DeficiencyModalBody Component
 *
 * Displays deficiency details in a modal body using the same card-based layout
 * as the DeficiencyQuotes view, but in read-only mode.
 */
const DeficiencyModalBody = ({ deficiency, isFetching, error }) => {
  // Handle loading state
  if (isFetching) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
      </div>
    );
  }

  // Handle missing deficiency data
  if (!deficiency) {
    console.error('[DeficiencyModalBody] No deficiency data provided');
    return (
      <div className="info-message">
        <FormattedMessage
          id="deficiency.error.noData"
          defaultMessage="No deficiency data available."
        />
      </div>
    );
  }

  // Validate deficiency has required _id field
  if (!deficiency._id) {
    console.error('[DeficiencyModalBody] Deficiency missing _id field:', deficiency);
    return (
      <div className="error-message">
        <FormattedMessage
          id="deficiency.error.invalidData"
          defaultMessage="Invalid deficiency data."
        />
      </div>
    );
  }

  // Format date using moment.js with error handling
  const formatDate = (dateString) => {
    if (!dateString) {
      return '-';
    }
    
    try {
      const formatted = moment(dateString).format(getDateFormat());
      // Check if date is valid
      if (formatted === 'Invalid date') {
        console.warn('[DeficiencyModalBody] Invalid date format:', dateString);
        return '-';
      }
      return formatted;
    } catch (err) {
      console.error('[DeficiencyModalBody] Error formatting date:', dateString, err);
      return '-';
    }
  };

  // Safely access nested objects with fallbacks
  const quote = deficiency.quote || {};
  const workOrder = deficiency.workOrder || {};

  // Safely filter parts and labours with error handling
  let parts = [];
  let labours = [];
  
  try {
    parts = Array.isArray(quote.parts) 
      ? quote.parts.filter((part) => part && !part.isDeleted)
      : [];
  } catch (err) {
    console.error('[DeficiencyModalBody] Error filtering parts:', err);
    parts = [];
  }

  try {
    labours = Array.isArray(quote.labours)
      ? quote.labours.filter((labour) => labour && !labour.isDeleted)
      : [];
  } catch (err) {
    console.error('[DeficiencyModalBody] Error filtering labours:', err);
    labours = [];
  }

  // Safely format price with error handling
  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return null;
    }
    
    try {
      const numPrice = Number(price);
      if (Number.isNaN(numPrice)) {
        console.warn('[DeficiencyModalBody] Invalid price value:', price);
        return null;
      }
      return numPrice.toFixed(2);
    } catch (err) {
      console.error('[DeficiencyModalBody] Error formatting price:', price, err);
      return null;
    }
  };

  const formattedPrice = formatPrice(quote.price);

  return (
    <div className="deficiency-modal-body">
      {/* Basic Info Card - Always at the top (no header, modal has title) */}
      <div className="card mr-bt-16">
        <div className="card-body">
          <div className="row">
            {/* Category */}
            <div className="col-md-6">
              <div className="form-group dis-flex f-dir-column">
                <div className="deficiency-readonly-field">
                  {deficiency.reportCategory ? (
                    <FormattedMessage
                      id={deficiency.reportCategory}
                      defaultMessage={deficiency.reportCategory}
                    />
                  ) : (
                    '-'
                  )}
                </div>
                <label>
                  <FormattedMessage id="Category" defaultMessage="Category" />
                </label>
              </div>
            </div>

            {/* Created At */}
            <div className="col-md-6">
              <div className="form-group dis-flex f-dir-column">
                <div className="deficiency-readonly-field">
                  {formatDate(deficiency.createdAt)}
                </div>
                <label>
                  <FormattedMessage id="Created At" defaultMessage="Created At" />
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Status */}
            <div className="col-md-4">
              <div className="form-group dis-flex f-dir-column">
                <div className="deficiency-readonly-field">
                  {deficiency.status ? (
                    <FormattedMessage
                      id={deficiency.status}
                      defaultMessage={deficiency.status}
                    />
                  ) : (
                    '-'
                  )}
                </div>
                <label>
                  <FormattedMessage id="Status" defaultMessage="Status" />
                </label>
              </div>
            </div>

            {/* Terms - from quote if available */}
            {quote.terms && (
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    {quote.terms}
                  </div>
                  <label>
                    <FormattedMessage id="Terms" defaultMessage="Terms" />
                  </label>
                </div>
              </div>
            )}

            {/* Price - from quote if available */}
            {formattedPrice !== null && (
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    ${formattedPrice}
                  </div>
                  <label>
                    <FormattedMessage
                      id="Price($)"
                      defaultMessage="Price($)"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Card - Always visible */}
      <div className="card mr-bt-16">
          <div className="card-header justify-content-between">
            <p>
              <FormattedMessage id="Quote" defaultMessage="Quote" />
            </p>
          </div>
          <div className="card-body">
            {/* Description */}
            {quote.description && (
              <div className="form-group dis-flex f-dir-column mr-bt-16">
                <div className="deficiency-readonly-field">
                  {quote.description}
                </div>
                <label>
                  <FormattedMessage
                    id="Describe Job"
                    defaultMessage="Describe Job"
                  />
                </label>
              </div>
            )}

            <div className="row">
              {/* Job Date - Always visible */}
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    {formatDate(quote.jobDate)}
                  </div>
                  <label>
                    <FormattedMessage
                      id="Job Date"
                      defaultMessage="Job Date"
                    />
                  </label>
                </div>
              </div>

              {/* Invoice No - Always visible */}
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    {quote.invoiceNo ?? '-'}
                  </div>
                  <label>
                    <FormattedMessage
                      id="Invoice No"
                      defaultMessage="Invoice No"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Work Order Card - Always visible */}
      <div className="card mr-bt-16">
          <div className="card-header justify-content-between">
            <p>
              <FormattedMessage id="Work Order" defaultMessage="Work Order" />
            </p>
          </div>
          <div className="card-body">
            {/* Description */}
            {workOrder.description && (
              <div className="form-group dis-flex f-dir-column mr-bt-16">
                <div className="deficiency-readonly-field">
                  {workOrder.description}
                </div>
                <label>
                  <FormattedMessage
                    id="Describe Job"
                    defaultMessage="Describe Job"
                  />
                </label>
              </div>
            )}

            <div className="row">
              {/* Invoice No - Always visible */}
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    {workOrder.invoiceNo ?? '-'}
                  </div>
                  <label>
                    <FormattedMessage
                      id="Invoice No"
                      defaultMessage="Invoice No"
                    />
                  </label>
                </div>
              </div>

              {/* Job Date - Always visible */}
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    {formatDate(workOrder.jobDate)}
                  </div>
                  <label>
                    <FormattedMessage
                      id="Job Date"
                      defaultMessage="Job Date"
                    />
                  </label>
                </div>
              </div>

              {/* PO Number - Always visible */}
              <div className="col-md-4">
                <div className="form-group dis-flex f-dir-column">
                  <div className="deficiency-readonly-field">
                    {workOrder.poNumber ?? '-'}
                  </div>
                  <label>
                    <FormattedMessage
                      id="PO Number"
                      defaultMessage="PO Number"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Parts and Labour Section - Always visible at the bottom */}
      <div className="deficiencymargintop">
        {/* Parts Card - No header */}
        <div className="parts-labour-card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table customeTable">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="PartName" defaultMessage="PartName" />
                    </th>
                    <th>
                      <FormattedMessage id="Quantity" defaultMessage="Quantity" />
                    </th>
                    <th>
                      <FormattedMessage id="Unit Price($)" defaultMessage="Unit Price($)" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parts.length > 0 ? (
                    parts.map((part, index) => {
                      // Validate part object
                      if (!part) {
                        console.warn('[DeficiencyModalBody] Null part at index:', index);
                        return null;
                      }
                      
                      const partPrice = formatPrice(part.price);
                      
                      return (
                        <tr key={part._id || index}>
                          <td>{part.partName || '-'}</td>
                          <td>{part.quantity || '-'}</td>
                          <td>${partPrice === null ? '0.00' : partPrice}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center">
                        <FormattedMessage
                          id="NO_PARTS_DATA"
                          defaultMessage="No parts added"
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Labour Card - No header */}
        <div className="parts-labour-card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table customeTable">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="Name" defaultMessage="Name" />
                    </th>
                    <th>
                      <FormattedMessage id="Labor Hours" defaultMessage="Labor Hours" />
                    </th>
                    <th>
                      <FormattedMessage id="Unit Price($)" defaultMessage="Unit Price($)" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {labours.length > 0 ? (
                    labours.map((labour, index) => {
                      // Validate labour object
                      if (!labour) {
                        console.warn('[DeficiencyModalBody] Null labour at index:', index);
                        return null;
                      }
                      
                      const labourPrice = formatPrice(labour.price);
                      
                      return (
                        <tr key={labour._id || index}>
                          <td>{labour.customerName || '-'}</td>
                          <td>{labour.workingHours || '-'}</td>
                          <td>${labourPrice === null ? '0.00' : labourPrice}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center">
                        <FormattedMessage
                          id="NO_LABOURS_DATA"
                          defaultMessage="No labour added"
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Report PDF Link - Icon in header */}
      {deficiency.reportPdfUrl && (
        <>
          <div className="deficiency-pdf-header-icon">
            <button
              type="button"
              className="deficiency-pdf-icon-large"
              onClick={() => window.open(deficiency.reportPdfUrl, '_blank', 'noopener,noreferrer')}
              aria-label="View Inspection Report"
            >
              <span 
                className="pdf-icon sprite-icon"
                data-tip="View Inspection Report"
                data-for="deficiency-pdf-header-tooltip"
              />
            </button>
          </div>
          <ToolTip id="deficiency-pdf-header-tooltip" place="left" />
        </>
      )}
    </div>
  );
};

DeficiencyModalBody.propTypes = {
  deficiency: PropTypes.shape({
    _id: PropTypes.string,
    status: PropTypes.string,
    reportType: PropTypes.string,
    description: PropTypes.string,
    reportCategory: PropTypes.string,
    createdAt: PropTypes.string,
    quote: PropTypes.shape({
      description: PropTypes.string,
      jobDate: PropTypes.string,
      invoiceNo: PropTypes.string,
      poNumber: PropTypes.string,
      parts: PropTypes.array,
      labours: PropTypes.array,
      terms: PropTypes.string,
      price: PropTypes.number,
    }),
    workOrder: PropTypes.shape({
      description: PropTypes.string,
      jobDate: PropTypes.string,
      invoiceNo: PropTypes.string,
      poNumber: PropTypes.string,
    }),
    reportPdfUrl: PropTypes.string,
  }),
  isFetching: PropTypes.bool,
  error: PropTypes.string,
};

DeficiencyModalBody.defaultProps = {
  deficiency: null,
  isFetching: false,
  error: null,
};

export default DeficiencyModalBody;
